/**
 * Extract dimension values and coordinates from enriched breakdowns
 * Handles grouping for all dimension types
 */

import {
  DimensionType,
  DimensionGroupingConfig,
} from '@/types/rebateAnalytics';
import { EnrichedBreakdown, CellCoordinates } from './types';

const MONTH_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  timeZone: 'UTC',
});

/**
 * Format a date as a month string (e.g., "Jan", "Feb")
 */
function formatMonthFromDate(date: Date): string {
  // Validate date before formatting
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    throw new Error(`Invalid date value: ${date}`);
  }
  return MONTH_FORMATTER.format(date);
}

/**
 * Apply grouping configuration to a single item
 * Returns array because an item might match multiple groups
 */
function applyGrouping(
  itemId: string,
  groupingConfig?: DimensionGroupingConfig,
): string[] {
  if (!groupingConfig) {
    return [itemId];
  }

  const matchedGroups: string[] = [];

  // Check if item matches any user groups first
  for (const group of groupingConfig.userGroups) {
    if (group.itemIds.includes(itemId)) {
      matchedGroups.push(group.name);
    }
  }

  // If no match, use catch-all group (always exists)
  if (matchedGroups.length === 0 && groupingConfig.catchAllGroups.length > 0) {
    matchedGroups.push(groupingConfig.catchAllGroups[0].name);
  }

  return matchedGroups;
}

/**
 * Apply tag grouping with support for conditional/unconditional bucketing
 * Returns array because tags can match multiple groups
 */
function applyTagGrouping(
  tags: Array<{ id: string; name: string }>,
  isConditional: boolean,
  dimensionType:
    | DimensionType.RULE_TAG
    | DimensionType.AGREEMENT_TAG
    | DimensionType.SUPPLIER_TAG,
  groupingConfig?: DimensionGroupingConfig,
): string[] {
  if (!groupingConfig) {
    // No grouping: return individual tag names
    if (tags.length === 0) {
      // No tags: use conditional/unconditional label
      if (dimensionType === DimensionType.RULE_TAG) {
        return [
          isConditional ? 'Conditional (Growth)' : 'Unconditional (Volume)',
        ];
      }
      return ['Standard'];
    }
    return tags.map((t) => t.name);
  }

  const matchedGroups: string[] = [];
  const tagIds = tags.map((t) => t.id);

  // Check if tags match any user groups first
  for (const group of groupingConfig.userGroups) {
    if (tagIds.some((tagId) => group.itemIds.includes(tagId))) {
      matchedGroups.push(group.name);
    }
  }

  // If we found matches, return them
  if (matchedGroups.length > 0) {
    return matchedGroups;
  }

  // No match: use catch-all group(s)
  if (dimensionType === DimensionType.RULE_TAG) {
    // RULE_TAG has two catch-all groups: conditional and unconditional
    if (groupingConfig.catchAllGroups.length >= 2) {
      const catchAllGroup = isConditional
        ? groupingConfig.catchAllGroups[0] // Conditional
        : groupingConfig.catchAllGroups[1]; // Unconditional
      matchedGroups.push(catchAllGroup.name);
    }
  } else {
    // AGREEMENT_TAG has one catch-all group
    if (groupingConfig.catchAllGroups.length > 0) {
      matchedGroups.push(groupingConfig.catchAllGroups[0].name);
    }
  }

  return matchedGroups;
}

/**
 * Get dimension value(s) for a given breakdown and dimension type
 * Returns array because some dimensions (tags) can produce multiple values
 */
export function getDimensionValues(
  breakdown: EnrichedBreakdown,
  dimension: DimensionType,
  groupingConfig?: DimensionGroupingConfig,
): string[] {
  switch (dimension) {
    case DimensionType.TIME:
      return [formatMonthFromDate(breakdown.date)];

    case DimensionType.SUPPLIER: {
      const values = applyGrouping(breakdown.supplierId, groupingConfig);
      // If no grouping, use name instead of ID
      return values.length > 0 && !groupingConfig
        ? [breakdown.supplierName]
        : values;
    }

    case DimensionType.DIVISION: {
      const values = applyGrouping(breakdown.divisionId, groupingConfig);
      // If no grouping, use name instead of ID
      return values.length > 0 && !groupingConfig
        ? [breakdown.divisionName]
        : values;
    }

    case DimensionType.RULE: {
      const values = applyGrouping(breakdown.ruleId, groupingConfig);
      // If no grouping, use name instead of ID
      return values.length > 0 && !groupingConfig
        ? [breakdown.ruleName]
        : values;
    }

    case DimensionType.AGREEMENT: {
      const values = applyGrouping(breakdown.agreementId, groupingConfig);
      // If no grouping, use name instead of ID
      return values.length > 0 && !groupingConfig
        ? [breakdown.agreementName]
        : values;
    }

    case DimensionType.RULE_TAG:
      return applyTagGrouping(
        breakdown.ruleTags,
        breakdown.isConditional,
        DimensionType.RULE_TAG,
        groupingConfig,
      );

    case DimensionType.AGREEMENT_TAG: {
      // Agreement tags don't have conditional/unconditional distinction
      return applyTagGrouping(
        breakdown.agreementTags,
        false,
        DimensionType.AGREEMENT_TAG,
        groupingConfig,
      );
    }

    case DimensionType.SUPPLIER_TAG: {
      // Supplier tags don't have conditional/unconditional distinction
      return applyTagGrouping(
        breakdown.supplierTags,
        false,
        DimensionType.SUPPLIER_TAG,
        groupingConfig,
      );
    }

    case DimensionType.RULE_TYPE: {
      // Rule type is simply Conditional or Unconditional based on isConditional flag
      // No grouping config needed since there are only 2 values
      return [breakdown.isConditional ? 'Conditional' : 'Unconditional'];
    }

    default:
      return [];
  }
}

/**
 * Extract all possible coordinate combinations from a breakdown
 * Returns array because tag dimensions can produce multiple coordinates
 * (e.g., a rule with 2 tags produces 2 sets of coordinates)
 *
 * @param breakdown - Enriched breakdown with tags attached
 * @param dimensions - 4-dimension configuration
 * @param groupingConfigs - Optional grouping configs for each dimension
 * @returns Array of coordinate sets (one per unique combination)
 */
export function extractCoordinates(
  breakdown: EnrichedBreakdown,
  dimensions: {
    primaryX: DimensionType;
    secondaryX?: DimensionType;
    primaryY: DimensionType;
    secondaryY?: DimensionType;
  },
  groupingConfigs?: Map<DimensionType, DimensionGroupingConfig>,
): CellCoordinates[] {
  // Get dimension values for each axis
  const primaryXValues = getDimensionValues(
    breakdown,
    dimensions.primaryX,
    groupingConfigs?.get(dimensions.primaryX),
  );

  const secondaryXValues = dimensions.secondaryX
    ? getDimensionValues(
        breakdown,
        dimensions.secondaryX,
        groupingConfigs?.get(dimensions.secondaryX),
      )
    : [null];

  const primaryYValues = getDimensionValues(
    breakdown,
    dimensions.primaryY,
    groupingConfigs?.get(dimensions.primaryY),
  );

  const secondaryYValues = dimensions.secondaryY
    ? getDimensionValues(
        breakdown,
        dimensions.secondaryY,
        groupingConfigs?.get(dimensions.secondaryY),
      )
    : [null];

  // If any dimension returns empty array, it means this breakdown should be filtered out
  if (
    primaryXValues.length === 0 ||
    primaryYValues.length === 0 ||
    (dimensions.secondaryX && secondaryXValues.length === 0) ||
    (dimensions.secondaryY && secondaryYValues.length === 0)
  ) {
    return [];
  }

  // Generate all combinations (Cartesian product)
  const coordinates: CellCoordinates[] = [];

  for (const column of primaryXValues) {
    for (const subColumn of secondaryXValues) {
      for (const row of primaryYValues) {
        for (const subRow of secondaryYValues) {
          coordinates.push({
            column,
            subColumn: subColumn === null ? null : subColumn,
            row,
            subRow: subRow === null ? null : subRow,
          });
        }
      }
    }
  }

  return coordinates;
}
